import axios, { AxiosRequestConfig } from 'axios'
import router from '@/router/index'
import { httpStatus } from './http-status'
import { useUserStore } from '@/store/modules/user'
import { ElMessage, ElMessageBox } from 'element-plus'

axios.defaults.headers.post['Content-Type'] = 'application/json;charset=UTF-8'
axios.defaults.headers.common['Remote-Host'] =
  import.meta.env.VITE_APP_BASE_DOMAIN || window.location.hostname

const http = axios.create({
  baseURL: `${import.meta.env.VITE_APP_BASE_API}`,
  timeout: 60 * 1000,
})

// 🔥 新增：防重复提示锁（全局唯一）
let isShowingError = false

// 🔥 新增：防重复跳转锁（全局唯一）
let isRedirecting = false

// 🔥 新增：跳转首页并强制刷新的通用方法
const redirectToHomeAndRefresh = () => {
  // 🔥 防重复调用：如果已经在跳转中，直接返回
  if (isRedirecting) {
    return
  }

  // 设置跳转锁
  isRedirecting = true

  // 先跳转到首页
  router
    .push({ path: '/dashboard' })
    .then(() => {
      // 跳转成功后强制刷新页面（两种方案可选，根据场景选择）
      // 方案1：Vue Router 刷新（仅刷新路由组件，不刷新整个页面）
      router.go(0)
      // 方案2：刷新整个页面（更彻底，适合需要重置所有状态的场景）
      // window.location.reload()
    })
    .catch(() => {
      // 跳转失败也要释放锁
      isRedirecting = false
    })

  // 🔥 设置超时释放锁（防止跳转失败导致锁一直占用）
  setTimeout(() => {
    isRedirecting = false
  }, 5000)
}

// 请求拦截器
http.interceptors.request.use(
  function (config: AxiosRequestConfig) {
    if (config.headers) {
      const userStore = useUserStore()
      const token = userStore.token
      if (token) {
        config.headers['Authorization'] = 'Bearer ' + token
      }
    }
    return config
  },
  error => {
    console.log(error.data.error.message)
    return Promise.reject(error.data.error.message)
  }
)

// 响应拦截器
http.interceptors.response.use(
  response => {
    const status = response.status
    const resData = response.data
    if (status === 200) {
      // 登录失效处理
      if (resData.code === 210 || resData.code === 3002) {
        // 🔥 登录失效提示加锁，避免重复弹出
        if (!isShowingError) {
          isShowingError = true
          ElMessage.warning('登录失效，请重新登录')
          // 释放锁（Message 提示默认 3 秒关闭，这里延迟释放）
          setTimeout(() => {
            isShowingError = false
          }, 3000)
        }
        // token失效清理
        const userStore = useUserStore()
        userStore.setUserInfo({})
        userStore.removeToken()
        // 🔥 跳转首页并强制刷新
        redirectToHomeAndRefresh()
        return Promise.reject(resData) // 统一返回reject，避免后续逻辑执行
      } else if (resData?.code !== 1000) {
        // 通信异常提示（非1000码）
        const { msg, ...resResult } = resData
        // 🔥 关键：加锁判断，只提示一次
        if (!isShowingError) {
          isShowingError = true
          ElMessageBox.confirm(msg, '提示', {
            confirmButtonText: '确认',
            type: 'warning',
            showCancelButton: false,
            showClose: false,
          }).finally(() => {
            // 🔥 弹窗关闭后释放锁（无论确认/关闭都释放）
            isShowingError = false
          })
        }
        return Promise.reject({ ...resResult, msg })
      } else {
        return Promise.resolve(resData)
      }
    } else if (httpExceptionHandler(response)) {
      // 🔥 HTTP状态码异常（如401/403/500）：跳转首页并刷新
      redirectToHomeAndRefresh()
      return Promise.reject(response) // 已处理异常，返回reject
    } else {
      return Promise.reject(response)
    }
  },
  function (error) {
    // http错误码处理（网络错误/超时等）
    console.error('axios接口返回错误：', error)
    // 🔥 网络异常也加锁，避免重复提示
    const errMsg = error.message || '请求失败，请稍后重试'
    if (!isShowingError) {
      isShowingError = true
      ElMessage.error(errMsg)
      setTimeout(() => {
        isShowingError = false
      }, 3000)
    }
    // 🔥 网络异常：跳转首页并刷新
    redirectToHomeAndRefresh()
    return Promise.reject(error)
  }
)

// 异常状态码处理
const httpExceptionHandler = (response: any): boolean => {
  const msg = httpStatus[response.status]
  if (msg) {
    console.error(response)
    // 🔥 HTTP状态码异常提示，加锁避免重复
    if (!isShowingError) {
      isShowingError = true
      ElMessage.error(`请求异常：${msg}`)
      setTimeout(() => {
        isShowingError = false
      }, 3000)
    }
    return true // 已处理
  }
  return false // 未处理
}

// 过滤空值字段
function obj2Params(params: any) {
  if (!params || Array.isArray(params)) return params
  const reqParams: any = {}
  Object.keys(params || {}).forEach((key: any) => {
    const val = params[key]
    if (val === 0 || val) {
      reqParams[key] = val
    }
  })
  return reqParams
}

export default http
